import Conviva from '@convivainc/conviva-js-coresdk';
import { ClientType, ColorSpace } from '@mlbtv-clients/services';
import { WIRELESS } from 'analytics/conviva/constants';
import { releaseConvivaAnalyticsSingleton } from 'analytics/conviva/hooks/useConviva/convivaAnalyticsSingleton';
import { EASTERN_TZ } from 'constants/date';
import { MODEL_YEAR_UNKNOWN } from 'constants/platforms';
import { ROUTES } from 'constants/screens';
import { ARROW_KEYS } from 'constants/spatialNavigation';
import { US_COUNTRY_CODE } from 'constants/userInfo';
import { ModelYearsConfig } from 'core-app/store/config';
import { ModelTier } from 'hooks/useDeviceModelTier/types';
import { getModelTier } from 'hooks/useDeviceModelTier/utils';
import { NavigateFunction } from 'react-router-dom';
import { EPGErrors } from 'services/epg/constants';
import fallback from 'store/config/fallback.json';

const { EPG_SERVICE_ERROR } = EPGErrors;

interface DeviceMetadata {
  deviceBrand: string;
  deviceManufacturer: string;
  deviceModel: string;
  operatingSystemName: string;
}

const {
  Constants: { DeviceCategory, DeviceMetadata, DeviceType },
} = Conviva;

const {
  BRAND,
  CATEGORY,
  MANUFACTURER,
  MODEL,
  OS_NAME,
  OS_VERSION,
  SCREEN_RESOLUTION_HEIGHT,
  SCREEN_RESOLUTION_WIDTH,
  TYPE,
} = DeviceMetadata;

// Disable window scrolling with arrow keys
globalThis.addEventListener('keydown', (e) => {
  if (ARROW_KEYS.has(e.keyCode)) {
    e.preventDefault();
  }
});

export const getClientType = async () => ClientType.AMAZON_VEGA;

export const getDeviceOSVersion = async (defaultOS: string) =>
  globalThis.__VEGA_SYSTEM_INFO__?.osVersion ?? defaultOS;

export const getDeviceAdParams = (adParams: Record<string, string>, ifaParams: unknown) => adParams;

export const getDeviceCaptionStyle = async () => {
  return `
    ::cue {
      color: cyan;
    }
  `;
};

export const getDeviceCountryCode = async () => US_COUNTRY_CODE;

export const getDeviceConnectionType = async () => WIRELESS;

export const getDeviceExitApp = () => {
  // Guard analytics cleanup so an error here can never block the actual exit below.
  try {
    releaseConvivaAnalyticsSingleton();
  } catch {
    // Intentionally swallowed.
  }

  // Primary exit channel: the native shell's Message Bridge
  // (globalThis.ReactNativeWebView.postMessage -> onMessage prop on <WebView>).
  globalThis.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'exitApp' }));

  // Fallback exit channel (onCloseWindow) for contexts without the bridge, e.g. a
  // plain-browser testing harness. Harmless to call alongside the bridge above.
  globalThis.close();
};

export const getDeviceExitConfirmation = (navigate?: NavigateFunction) => {
  if (navigate) navigate(ROUTES.EXIT);
};

export const getDeviceId = async (deviceId: string) => deviceId;

export const getDeviceIdType = () => 'dpid';

export const getDeviceKeyboardIsOpen = async () => false;

export const getDeviceMetadata = async (deviceMetadata: DeviceMetadata) => {
  const { deviceBrand, deviceManufacturer, deviceModel, operatingSystemName } = deviceMetadata;

  return {
    [BRAND]: globalThis.__VEGA_SYSTEM_INFO__?.brand ?? deviceBrand,
    [CATEGORY]: DeviceCategory.AMAZON,
    [MANUFACTURER]: globalThis.__VEGA_SYSTEM_INFO__?.manufacturer ?? deviceManufacturer,
    [MODEL]: globalThis.__VEGA_SYSTEM_INFO__?.model ?? deviceModel,
    [OS_NAME]: globalThis.__VEGA_SYSTEM_INFO__?.osName ?? operatingSystemName,
    [OS_VERSION]: globalThis.__VEGA_SYSTEM_INFO__?.osVersion ?? '',
    [SCREEN_RESOLUTION_HEIGHT]: globalThis.screen.height,
    [SCREEN_RESOLUTION_WIDTH]: globalThis.screen.width,
    [TYPE]: DeviceType.SETTOP,
  };
};

export const getDeviceModelTier = async (modelTiers: Record<string, ModelTier>) =>
  getModelTier(modelTiers, '');

export const getDeviceModelYear = async (_modelYearsConfig: ModelYearsConfig) => MODEL_YEAR_UNKNOWN;

export const getDeviceHasSpinner = async (
  hasSpinner: typeof fallback.featureFlags.animations.hasSpinner,
) => !hasSpinner.hasAllModelsDisabled;

export const getDevicePlaybackCapabilities = async () => ({ colorSpace: [ColorSpace.HDR] });

export const getDeviceModel = async (defaultValue: string) => defaultValue;

export const getDeviceSKU = async (defaultValue: string) => defaultValue;

export const getDeviceTimeZone = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (!timeZone) {
    throw new Error(`${EPG_SERVICE_ERROR} Time zone not found. Falling back to ${EASTERN_TZ}`);
  }

  return timeZone;
};

export const getDeviceTTSEnabled = () => false;
